import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '9c75b840d7cb809a3621c1abdc515ee6';
const MIXPANEL_API_HOST = 'https://api-eu.mixpanel.com';

mixpanel.init(MIXPANEL_TOKEN, {
  api_host: MIXPANEL_API_HOST,
});

type StateCallback = (state: {
  consent: ConsentValue;
  disabled: boolean;
}) => void;

export type ConsentValue = 'all' | 'necessary' | 'none';

class MixpanelHandler {
  private consent: ConsentValue = 'none';

  private subscriptions: Map<string, StateCallback> = new Map();

  constructor() {
    // Initialize consent from stored value
    this.initializeConsent();
  }

  private initializeConsent() {
    // Check for stored consent in cookies
    const cookies = document.cookie.split(';');
    const consentCookie = cookies.find(cookie => cookie.trim().startsWith('consent-agreement='));
    
    if (consentCookie) {
      const consentValue = consentCookie.split('=')[1] as ConsentValue;

      if (consentValue && ['all', 'necessary', 'none'].includes(consentValue)) {
        this.setConsent(consentValue);
      }
    }
  }

  identify(id?: string) {
    if (id) {
      mixpanel.identify(id);
    }
  }

  track(name: string, props?: { [key: string]: any }) {
    mixpanel.track(name, props);
  }

  subscribeToState(id: string, callback: StateCallback) {
    this.subscriptions.set(id, callback);
  }

  unsubscribeFromState(id: string) {
    this.subscriptions.delete(id);
  }

  enableAll() {
    this.consent = 'all';
    mixpanel.opt_in_tracking();

    this.notifySubscribers();
  }

  enableNecessaryOnly() {
    this.consent = 'necessary';

    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: MIXPANEL_API_HOST,
      ip: false,
      disable_persistence: true,
      debug: false,
    });

    mixpanel.clear_opt_in_out_tracking();

    this.notifySubscribers();
  }

  disableAll() {
    this.consent = 'none';
    mixpanel.clear_opt_in_out_tracking();
    mixpanel.opt_out_tracking();

    this.notifySubscribers();
  }

  private notifySubscribers() {
    const state = {
      consent: this.consent,
      disabled: this.consent !== 'all',
    };

    this.subscriptions.forEach((callback) => callback(state));
  }

  disabled(): boolean {
    return this.consent !== 'all';
  }

  getConsent(): ConsentValue {
    return this.consent;
  }

  initializeConsentFromStorage(): void {
    this.initializeConsent();
  }

  setConsent(value: ConsentValue) {
    switch (value) {
      case 'all':
        this.enableAll();
        break;
      case 'necessary':
        this.enableNecessaryOnly();
        break;
      case 'none':
        this.disableAll();
        break;
    }
  }
}

export const Mixpanel = new MixpanelHandler();
